import { ErrorBanner, HelpBanner } from '@/components';
import DatePicker, { buildCurrentCycleRange, CURRENT_CYCLE_TITLE } from '@/components/DatePicker';
import { HelpItem } from '@/components/HelpBanner';
import Layout from '@/components/Layout';
import { AccountPlan } from '@/constants/enum';
import { FileExtension, FileTypes } from '@/constants/file';
import { cleanObject, exportExtensionFile, exportListShipmentsFile, getExportFileName } from '@/helpers';
import { isLockFeature } from '@/helpers/feature';
import { useDebounce } from '@/hooks';
import { useUpgradeTracking } from '@/hooks/useUpgradeTracking';
import { usePlanDisplayName } from '@/hooks/usePlanDisplayName';
import { useGetShipmentSearchQuery, useGetUserPlanQuery } from '@/redux/api/api.caller';
import shipmentSlice, {
  datePickerSelector,
  shipmentFilterSelector,
  titleDatePickerSelector,
} from '@/redux/features/shipment.slice';
import { ActionList, BlockStack, Button, ButtonGroup, Modal, Popover, Tooltip, useBreakpoints } from '@shopify/polaris';
import { RedoIcon, UploadIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import ShipmentTab from './components/ShipmentTab';
import ShipmentTable from './components/ShipmentTable';
import ShipmentsAiBanner from './components/ShipmentsAiBanner';
import SyncPastOrdersModal from './components/SyncPastOrdersModal';
import { StyledGroupBtn } from './styled';
import { statusFromParam } from './helpers/statusFromParam';

interface IExtensionBtn {
  loading: boolean;
  type: FileExtension;
}

const customHelpItems: HelpItem[] = [
  { id: '1', label: 'Have these issues?', showBullet: false },
  { id: '2', label: 'Information is not up-to-date', showBullet: true },
  { id: '3', label: 'Information is not correct', showBullet: true },
  { id: '4', label: 'Carrier is not detected', showBullet: true },
  { id: '5', label: 'Request a carrier integration', showBullet: true },
];

const Shipments = () => {
  const location = useLocation();
  const locationState = location?.state;
  useEffect(() => {
    return () => {
      if (locationState && locationState.from === 'shipmentsOverview' && locationState.previousFilter) {
        const { shipmentFilter, shipmentDatePicker, shipmentTitleDatePicker } = locationState.previousFilter;
        dispatch(shipmentSlice.actions.handleChangeTableFilter(shipmentFilter));
        dispatch(shipmentSlice.actions.handleDatePicker(shipmentDatePicker));
        dispatch(shipmentSlice.actions.handleTitleDatePicker(shipmentTitleDatePicker));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isSyncPastOpen, setSyncPastOpen] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>('');
  const [extensionBtn, setExtensionBtn] = useState<IExtensionBtn>({ loading: false, type: FileExtension.XLSX });
  const dispatch = useDispatch();
  const { start, end } = useSelector(datePickerSelector);
  const titleDatePicker = useSelector(titleDatePickerSelector);
  const { search, couriers, status: shipmentStatus, statusIn, } = useSelector(shipmentFilterSelector);
  const isLockStarter = isLockFeature([AccountPlan.Starter]);
  const { getPlanName } = usePlanDisplayName();
  const [isOpenModalUpgrade, setIsOpenModalUpgrade] = useState<boolean>(false);
  const { navigateToPlan } = useUpgradeTracking();
  // BFS TR-437: page primary-action bar overflows at ≤ 390px (Resync orders clipped).
  // Keep Polaris "no wrap" on tablet+ but allow wrap on mobile (xs/sm) so all three buttons fit.
  const { smDown } = useBreakpoints();

  const handleApplyDatePicker = (start: Date, end: Date) => dispatch(shipmentSlice.actions.handleDatePicker({ start, end }));
  const handleSaveDatePickerTitle = (titleBtn: string) => dispatch(shipmentSlice.actions.handleTitleDatePicker(titleBtn));

  const [exportPopoverActive, setExportPopoverActive] = useState(false);
  const toggleExportPopover = () => setExportPopoverActive(!exportPopoverActive);

  const handleSyncedSuccess = () => {
    if (apiError) setApiError('');
  };

  const handleExport = (fileExtension: FileExtension) => {
    if (isLockStarter) {
      return handleOpenModal();
    }
    handleExportShipmentFile(fileExtension);
  };

  const handleExportShipmentFile = async (extension: FileExtension) => {
    if (isLockStarter) {
      return;
    }
    try {
      setExtensionBtn((prev) => ({ ...prev, type: extension, loading: true }));
      const { data, status } = await exportListShipmentsFile(
        cleanObject({
          extension,
          fromDate: start.toISOString(),
          toDate: end.toISOString(),
          search,
          courier: couriers.length > 0 ? couriers.join(',') : undefined,
          status: shipmentStatus,
          statusIn: statusIn.length > 0 ? statusIn.join(',') : undefined,
          sortBy: shipmentFilter.sortBy,
          sortDirection: shipmentFilter.sortBy ? shipmentFilter.sortOrder : '',
          type: shipmentFilter.type,
          isSlowDispatched: isSlowDispatched === 'true',
        }),
      );
      if (status === 200 && data) {
        const fileName = getExportFileName('Order', start, end);
        const fileType = FileTypes[extension.toUpperCase() as keyof typeof FileTypes];
        exportExtensionFile(data, fileName, fileType);
      }
    } catch (error) {
    } finally {
      setExtensionBtn((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleUpgradeNow = () => {
    navigateToPlan({
      trigger_type: 'paywall',
      trigger_element: 'Upgrade Now',
      feature: 'export_shipments',
    });
  };

  const handleCloseModal = () => {
    setIsOpenModalUpgrade(false);
  };

  const handleOpenModal = () => {
    setIsOpenModalUpgrade(true);
  };

  const [searchParams] = useSearchParams();
  const isSlowDispatched = searchParams.get('slow_dispatch');
  const shipmentFilter = useSelector(shipmentFilterSelector);
  const didHydrateStatus = useRef(false);
  useEffect(() => {
    if (didHydrateStatus.current) return;
    const urlStatus = statusFromParam(searchParams.get('status'));
    if (!urlStatus) return;
    didHydrateStatus.current = true;
    dispatch(
      shipmentSlice.actions.handleChangeTableFilter({
        ...shipmentFilter,
        status: urlStatus,
        page: 1,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const debouncedSearch = useDebounce<string>(shipmentFilter.search);
  const isCurrentCycle = titleDatePicker === CURRENT_CYCLE_TITLE;
  const unRequiredParams = cleanObject({
    page: shipmentFilter.page,
    perPage: shipmentFilter.pageSize,
    fromDate: isCurrentCycle ? undefined : start.toISOString(),
    toDate: isCurrentCycle ? undefined : end.toISOString(),
    inCycle: isCurrentCycle ? true : undefined,
    search: debouncedSearch,
    couriers: shipmentFilter.couriers.length > 0 ? shipmentFilter.couriers.join(',') : undefined,
    status: shipmentFilter.status,
    statusIn: shipmentFilter.statusIn.length > 0 ? shipmentFilter.statusIn.join(',') : undefined,
    sortBy: shipmentFilter.sortBy,
    sortDirection: shipmentFilter.sortBy ? shipmentFilter.sortOrder : '',
    type: shipmentFilter.type,
    isSlowDispatched: isSlowDispatched === 'true',
  });
  const {
    data: shipmentData,
    isFetching: shipmentFetching,
    isLoading: shipmentLoading,
  } = useGetShipmentSearchQuery({
    ...unRequiredParams,
  });

  const { data: userPlanData } = useGetUserPlanQuery({});
  const cycleStart = userPlanData?.data?.currentCycle?.cycleStart;
  const cycleEnd = userPlanData?.data?.currentCycle?.cycleEnd;
  const currentCycleRange = useMemo(
    () => buildCurrentCycleRange(cycleStart, cycleEnd),
    [cycleStart, cycleEnd],
  );

  return (
    <Layout
      fullWidth
      maxWidth="100rem"
      title="List shipments"
      primaryAction={
        <ButtonGroup noWrap={!smDown}>
          <StyledGroupBtn>
            <Popover
              active={exportPopoverActive}
              activator={
                <Tooltip
                  zIndexOverride={isLockStarter ? 400 : -1000}
                  content={isLockStarter && 'Upgrade plan to unlock this feature'}
                >
                  <Button
                    disclosure
                    icon={UploadIcon}
                    onClick={toggleExportPopover}
                    loading={extensionBtn.loading}
                    disabled={isLockStarter || extensionBtn.loading}
                  >
                    Export
                  </Button>
                </Tooltip>
              }
              autofocusTarget="first-node"
              onClose={toggleExportPopover}
            >
              <ActionList
                actionRole="menuitem"
                items={[
                  {
                    content: 'Excel',
                    onAction: () => handleExport(FileExtension.XLSX),
                  },
                  {
                    content: 'CSV',
                    onAction: () => handleExport(FileExtension.CSV),
                  },
                ]}
              />
            </Popover>

            <DatePicker
              titleBtn={titleDatePicker}
              start={start}
              end={end}
              onSave={handleApplyDatePicker}
              setTitleBtn={handleSaveDatePickerTitle}
              extraRanges={currentCycleRange}
            />
          </StyledGroupBtn>
          <Button icon={RedoIcon} onClick={() => setSyncPastOpen(true)}>
            Resync orders
          </Button>
        </ButtonGroup>
      }
    >
      <BlockStack gap="400">
        <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
        <ShipmentsAiBanner />
        <ShipmentTab data={shipmentData}>
          <ShipmentTable
            data={shipmentData}
            isLoading={shipmentLoading}
            isFetching={shipmentFetching}
            unRequiredParams={unRequiredParams}
          />
        </ShipmentTab>
        <HelpBanner id="c5e7285d-7e4c-4ad8-8a5b-54625320c444" title="We’re here to help" helpItems={customHelpItems} />
      </BlockStack>
      <SyncPastOrdersModal open={isSyncPastOpen} onClose={() => setSyncPastOpen(false)} onSynced={handleSyncedSuccess} />
      {isLockStarter && (
        <Modal
          open={isOpenModalUpgrade}
          onClose={handleCloseModal}
          title="Upgrade to use this feature"
          primaryAction={{
            content: 'Upgrade Now',
            onAction: handleUpgradeNow,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: handleCloseModal,
            },
          ]}
        >
          <Modal.Section>
            This feature is only available on the paid plan. You need to upgrade to the {getPlanName(AccountPlan.Basic)} or
            higher plan to use this feature.
          </Modal.Section>
        </Modal>
      )}
    </Layout>
  );
};

export default Shipments;
